import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiGetBadgeUrl,
    type OpenApiBatchUpdateBadgesRequest,
    type OpenApiBatchUpdateBadgesResponseSuccess,
    type OpenApiCreateBadgeRequest,
    type OpenApiCreateBadgeResponseSuccess,
    type OpenApiDeleteBadgesRequest,
    type OpenApiDeleteBadgesResponseSuccess,
    OpenApiGetBadgeMethod,
    type OpenApiGetBadgePathParameters,
    type OpenApiGetBadgeResponseSuccess,
    type OpenApiGetBadgesResponseSuccess,
    type OpenApiGetBadgesSearchParams,
    type OpenApiUpdateBadgePathParameters,
    type OpenApiUpdateBadgeRequest,
    type OpenApiUpdateBadgeResponseSuccess,
} from '#shared/api/schema';

const BadgesTag = 'Badges' as const;

export const badgesApi = createApi({
    name: 'entities/badge/api/badges',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [BadgesTag],
    endpoints: (builder) => {
        return {
            updateBadge: builder.mutation<
                OpenApiUpdateBadgeResponseSuccess,
                { body: OpenApiUpdateBadgeRequest; params: OpenApiUpdateBadgePathParameters }
            >({
                query: ({ params: { id }, body }) => {
                    return {
                        url: `/badges/${id}`,
                        method: 'PATCH',
                        body,
                    };
                },
                async onQueryStarted(arg, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    badgesApi.endpoints.getAllBadges.updateQueryData({}, (draft) => {
                        draft.items = draft.items.map((badge) =>
                            badge.id === arg.params.id ? { ...badge, ...arg.body } : badge
                        );
                    });
                    badgesApi.endpoints.getBadgeById.updateQueryData({ params: { id: arg.params.id } }, (draft) => {
                        return { ...draft, ...arg.body };
                    });
                },
            }),
            getAllBadges: builder.query<OpenApiGetBadgesResponseSuccess, { params?: OpenApiGetBadgesSearchParams }>({
                query: ({ params }) => {
                    return {
                        url: `/badges`,
                        params,
                        method: 'GET',
                    };
                },
                providesTags: [BadgesTag],
                // Добавляем инвалидацию бейджей, они могут обновиться при импорте из внешних источников — 1C/МойСклад/WB
                keepUnusedDataFor: 5,
            }),
            updateBadges: builder.mutation<OpenApiBatchUpdateBadgesResponseSuccess, OpenApiBatchUpdateBadgesRequest>({
                query: (payload) => {
                    return {
                        url: `/badges`,
                        method: 'PATCH',
                        body: payload,
                    };
                },
                invalidatesTags: [BadgesTag],
            }),
            deleteBadges: builder.mutation<OpenApiDeleteBadgesResponseSuccess, OpenApiDeleteBadgesRequest>({
                query: (payload) => {
                    return {
                        url: `/badges`,
                        method: 'DELETE',
                        body: payload,
                    };
                },
                invalidatesTags: [BadgesTag],
                async onQueryStarted({ badge_ids }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const deletedBadges = new Set(badge_ids);

                    badgesApi.endpoints.getAllBadges.updateQueryData({}, (draft) => {
                        draft.items = draft.items.filter((badge) => !deletedBadges.has(badge.id));
                    });
                },
            }),
            createBadge: builder.mutation<OpenApiCreateBadgeResponseSuccess, OpenApiCreateBadgeRequest>({
                query: (payload) => {
                    return {
                        url: `/badges`,
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: [BadgesTag],
            }),
            getBadgeById: builder.query<OpenApiGetBadgeResponseSuccess, { params: OpenApiGetBadgePathParameters }>({
                query: ({ params }) => {
                    return {
                        url: getOpenApiGetBadgeUrl(params),
                        method: OpenApiGetBadgeMethod,
                    };
                },
                providesTags: [BadgesTag],
            }),
        };
    },
});

export const useAllBadges = badgesApi.useGetAllBadgesQuery;
export const getAllBadges = badgesApi.endpoints.getAllBadges.query;
export const useLazyGetBadgeById = badgesApi.useLazyGetBadgeByIdQuery;
export const useGetBadgeById = badgesApi.useGetBadgeByIdQuery;
export const updateBadges = badgesApi.endpoints.updateBadges.mutate;
export const deleteBadges = badgesApi.endpoints.deleteBadges.mutate;
export const createBadge = badgesApi.endpoints.createBadge.mutate;
export const updateBadge = badgesApi.endpoints.updateBadge.mutate;
